import React, { FunctionComponent, useEffect } from 'react';
import { Button, Form, Input } from 'antd';
import { MailOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import { AppStore, userActions, UserStatus } from '@akademiasaas/shared';
import { useAppDispatch } from '~/initializeStore';
import { formLayout } from '~/components/ui';
import { useQuery } from '~/hooks/useQuery';
import { safeContinuePath } from '~/utils/safeContinuePath';
import AuthCard, { AuthHeading, AUTH_TITLE_ID } from '../../layout/AuthCard';
import AuthErrorCallout, { isCalloutKind } from '../../form/AuthErrorCallout';
import PasswordInput from '../../form/PasswordInput';
import SubmitButton from '../../form/SubmitButton';
import { authErrorKind } from '../../form/authErrors';
import { emailRules, requiredRule } from '../../form/authRules';
import styles from '../../form/authForm.module.scss';

type LoginFormModel = {
  email: string;
  password: string;
};

const FORM_NAME = 'login';

const Login: FunctionComponent = () => {
  const { t } = useTranslation(['auth']);
  const dispatch = useAppDispatch();
  const history = useHistory();
  const query = useQuery();
  const [form] = Form.useForm<LoginFormModel>();
  const email = (Form.useWatch('email', form) ?? '').trim();
  const { status, error } = useSelector((store: AppStore) => store.user);
  const busy = status === UserStatus.LOGGING_IN;
  // Błąd bez kodu (spoza Firebase) też jest błędem — pokazujemy ogólny komunikat.
  const errorKind = status === UserStatus.HAS_ERROR ? (authErrorKind(error) ?? 'unknown') : null;

  // Błąd z innego ekranu /auth nie powinien witać użytkownika na logowaniu.
  useEffect(() => {
    dispatch(userActions.resetErrors());
  }, [dispatch]);

  const onFinish = (values: LoginFormModel) => {
    if (busy) return;
    dispatch(
      userActions.logInUser({ email: values.email.trim(), password: values.password }, () =>
        history.push(safeContinuePath(query.get('continue')))
      )
    );
  };

  const forgotPath = `/auth/forgot-password${email ? `/${encodeURIComponent(email)}` : ''}`;

  return (
    <AuthCard
      footer={
        <>
          {t('auth:screens.login.noAccount')}{' '}
          <Link to="/auth/register">{t('auth:screens.login.register')}</Link>
        </>
      }
    >
      <AuthHeading title={t('auth:screens.login.title')} lead={t('auth:screens.login.lead')} />
      {errorKind && (
        <AuthErrorCallout kind={isCalloutKind(errorKind) ? errorKind : 'unknown'} email={email} />
      )}
      <Form<LoginFormModel>
        form={form}
        name={FORM_NAME}
        layout="vertical"
        requiredMark={false}
        scrollToFirstError={{ focus: true }}
        validateTrigger="onBlur"
        className={`${formLayout.form} ${styles.form}`}
        aria-labelledby={AUTH_TITLE_ID}
        onFinish={onFinish}
      >
        <Form.Item name="email" label={t('auth:screens.field.email')} rules={emailRules(t)}>
          <Input
            type="email"
            autoComplete="email"
            aria-required="true"
            autoFocus={!errorKind}
            readOnly={busy}
          />
        </Form.Item>
        <div>
          <div className={styles.labelRow}>
            <label htmlFor={`${FORM_NAME}_password`}>{t('auth:screens.field.password')}</label>
            <Link to={forgotPath}>{t('auth:screens.login.forgot')}</Link>
          </div>
          <Form.Item
            name="password"
            rules={requiredRule(t('auth:screens.validation.passwordRequired'), {
              allowWhitespace: true,
            })}
          >
            <PasswordInput autoComplete="current-password" aria-required="true" readOnly={busy} />
          </Form.Item>
        </div>
        <SubmitButton
          label={t('auth:screens.login.submit')}
          busyLabel={t('auth:screens.login.busy')}
          busy={busy}
        />
      </Form>
      <div className={styles.or}>{t('auth:screens.login.or')}</div>
      <div className={styles.actions}>
        <Button
          block
          icon={<MailOutlined aria-hidden />}
          onClick={() =>
            history.push(`/auth/login-by-link${email ? `?email=${encodeURIComponent(email)}` : ''}`)
          }
        >
          {t('auth:screens.login.byLink')}
        </Button>
      </div>
    </AuthCard>
  );
};

export default Login;
